import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const NotificationPopup = () => {
  const [notifications, setNotifications] = useState([])
  const latestRentalNotification = useSelector((state) => state.general.latestRentalNotification)
  const latestDepositNotification = useSelector((state) => state.general.latestDepositNotification)

  // Watch for new rental notifications
  useEffect(() => {
    if (latestRentalNotification) {
      const id = `rental-${latestRentalNotification}`
      setNotifications((prev) => {
        // Prevent duplicates
        if (prev.some((n) => n.id === id)) return prev
        return [
          ...prev,
          {
            id,
            type: 'rental',
            message: '🚗 New Rental Booking Received!',
            subMessage: 'Check the Rental tab for details',
            timestamp: Date.now(),
          },
        ]
      })
    }
  }, [latestRentalNotification])

  // Watch for new deposit notifications
  useEffect(() => {
    if (latestDepositNotification) {
      const id = `deposit-${latestDepositNotification}`
      setNotifications((prev) => {
        if (prev.some((n) => n.id === id)) return prev
        return [
          ...prev,
          {
            id,
            type: 'deposit',
            message: '💰 New Deposit Received!',
            subMessage: 'Check the Drivers-Deposit tab for details',
            timestamp: Date.now(),
          },
        ]
      })
    }
  }, [latestDepositNotification])

  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (notifications.length === 0) return
    const timer = setTimeout(() => {
      setNotifications((prev) => prev.slice(1))
    }, 6000)
    return () => clearTimeout(timer)
  }, [notifications])

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  if (notifications.length === 0) return null

  return (
    <div style={styles.container}>
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          style={{
            ...styles.popup,
            ...(notification.type === 'rental' ? styles.rentalPopup : styles.depositPopup),
            animationDelay: `${index * 0.1}s`,
          }}
        >
          <div style={styles.popupContent}>
            <div style={styles.iconContainer}>
              <div
                style={{
                  ...styles.iconCircle,
                  background:
                    notification.type === 'rental'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                }}
              >
                {notification.type === 'rental' ? '🚗' : '💰'}
              </div>
            </div>
            <div style={styles.textContainer}>
              <div style={styles.messageTitle}>{notification.message}</div>
              <div style={styles.messageSubtext}>{notification.subMessage}</div>
            </div>
            <button
              style={styles.closeButton}
              onClick={() => dismissNotification(notification.id)}
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
          <div
            style={{
              ...styles.progressBar,
              background:
                notification.type === 'rental'
                  ? 'linear-gradient(90deg, #667eea, #764ba2)'
                  : 'linear-gradient(90deg, #f093fb, #f5576c)',
            }}
          />
        </div>
      ))}

      <style>
        {`
          @keyframes notifSlideIn {
            0% {
              transform: translateX(120%);
              opacity: 0;
            }
            60% {
              transform: translateX(-8px);
              opacity: 1;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes notifProgressShrink {
            from { width: 100%; }
            to { width: 0%; }
          }

          @keyframes notifPulseGlow {
            0%, 100% {
              box-shadow: 0 4px 24px rgba(102, 126, 234, 0.25);
            }
            50% {
              box-shadow: 0 4px 32px rgba(102, 126, 234, 0.45);
            }
          }
        `}
      </style>
    </div>
  )
}

const styles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '400px',
    width: '100%',
    pointerEvents: 'none',
  },
  popup: {
    background: '#ffffff',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    animation: 'notifSlideIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, notifPulseGlow 2s ease-in-out 3',
    pointerEvents: 'all',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  rentalPopup: {
    borderLeft: '4px solid #667eea',
  },
  depositPopup: {
    borderLeft: '4px solid #f5576c',
  },
  popupContent: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 16px',
    gap: '12px',
  },
  iconContainer: {
    flexShrink: 0,
  },
  iconCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  messageTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1a1a2e',
    lineHeight: 1.3,
    marginBottom: '2px',
  },
  messageSubtext: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: 1.3,
  },
  closeButton: {
    flexShrink: 0,
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '6px',
    lineHeight: 1,
    transition: 'all 0.2s',
  },
  progressBar: {
    height: '3px',
    animation: 'notifProgressShrink 6s linear forwards',
  },
}

export default NotificationPopup
